import 'core-js/stable'
import 'regenerator-runtime/runtime'

import React from 'react'
import ReactDOM from 'react-dom/client'
import {ApolloLink, ApolloClient, InMemoryCache, ApolloProvider, HttpLink} from '@apollo/client'
import {onError} from '@apollo/client/link/error'
import {ElementID} from '../shared/elementID'
import {ClientSettings} from '../shared/types'
import {RouteProvider} from './route'
import {AuthProvider} from './authContext'
import {LocalStorageKey} from './utility'
import {App} from './app'

import './i18n'

// See: https://www.apollographql.com/docs/react/data/fragments/#fragments-on-unions-and-interfaces
export async function fetchIntrospectionQueryResultData(url: string) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      variables: {},
      query: `
      {
        __schema {
          types {
            kind
            name
            possibleTypes {
              name
            }
          }
        }
      }
    `,
    }),
  })

  const result = await response.json()

  const possibleTypes: any = {}

  result.data.__schema.types.forEach((supertype: any) => {
    if (supertype.possibleTypes) {
      possibleTypes[supertype.name] = supertype.possibleTypes.map((subtype: any) => subtype.name)
    }
  })

  return possibleTypes
}

export const initClient = async () => {
  const {apiURL}: ClientSettings = JSON.parse(
    document.getElementById(ElementID.Settings)!.textContent!
  )

  const adminAPIURL = `${apiURL}/admin`

  const authLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem(LocalStorageKey.SessionToken)
    const context = operation.getContext()

    operation.setContext({
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        ...context.headers,
      },
      ...context,
    })

    return forward(operation)
  })

  const authErrorLink = onError(({graphQLErrors, operation, forward}) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({extensions}) => {
        if (extensions?.code === 'UNAUTHENTICATED') {
          window.location.pathname = '/logout'
        }
      })
    }
    return forward(operation)
  })

  const client = new ApolloClient({
    link: ApolloLink.from([
      authLink,
      authErrorLink,
      new HttpLink({
        uri: adminAPIURL,
        headers: {
          'Apollo-Require-Preflight': 'true',
        },
      }),
    ]),
    cache: new InMemoryCache({
      possibleTypes: await fetchIntrospectionQueryResultData(adminAPIURL),
    }),
    headers: {
      'Apollo-Require-Preflight': 'true',
    },
  })

  window.addEventListener('dragover', (e) => e.preventDefault())
  window.addEventListener('drop', (e) => e.preventDefault())

  const root = ReactDOM.createRoot(document.getElementById(ElementID.ReactRoot) as HTMLElement)
  root.render(
    <React.StrictMode>
      <ApolloProvider client={client}>
        <AuthProvider>
          <RouteProvider>
            <App />
          </RouteProvider>
        </AuthProvider>
      </ApolloProvider>
    </React.StrictMode>
  )
}
